import type {
  EventDateRange,
  EventItem,
  EventsCalendarMap,
  UploadEventImageResponse,
} from '../types/event.types';

function readString(value: unknown, fallback = ''): string {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return fallback;
}

function readNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return fallback;
}

function readNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
}

function readNullableString(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  const trimmed = readString(value).trim();
  return trimmed.length > 0 ? trimmed : null;
}

function readStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item) => readString(item).trim())
    .filter((item) => item.length > 0);
}

function readBoolean(value: unknown, fallback = true): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1') {
      return true;
    }
    if (normalized === 'false' || normalized === '0') {
      return false;
    }
  }
  if (typeof value === 'number') {
    if (value === 1) {
      return true;
    }
    if (value === 0) {
      return false;
    }
  }
  return fallback;
}

function readWeekdays(value: unknown): number[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((day) => Number(day))
    .filter((day) => Number.isInteger(day) && day >= 1 && day <= 7);
}

function readDateRanges(value: unknown): EventDateRange[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const result: EventDateRange[] = [];
  value.forEach((raw, index) => {
    if (!raw || typeof raw !== 'object') {
      return;
    }
    const record = raw as Record<string, unknown>;
    const startEventDate = readNullableString(
      record.startEventDate ?? record.start_event_date,
    );
    const endEventDate = readNullableString(
      record.endEventDate ?? record.end_event_date,
    );
    if (!startEventDate || !endEventDate) {
      return;
    }
    const eventDateId =
      readNumber(record.eventDateId ?? record.event_date_id, 0) || undefined;
    const range: EventDateRange = {
      id:
        eventDateId !== undefined
          ? `edr-${String(eventDateId)}`
          : `edr-${String(index)}-${startEventDate}`,
      startEventDate,
      endEventDate,
    };
    if (eventDateId !== undefined) {
      range.eventDateId = eventDateId;
    }
    result.push(range);
  });
  return result;
}

export function normalizeEvent(raw: unknown): EventItem | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const record = raw as Record<string, unknown>;
  const eventId =
    readNumber(record.eventId) ||
    readNumber(record.id) ||
    readNumber(record.event_id);
  const eventCode =
    readString(record.eventCode).trim() ||
    readString(record.event_code).trim() ||
    (eventId ? String(eventId) : '');

  if (!eventId) {
    return null;
  }

  const eventImageRaw = record.eventImage ?? record.event_image;
  const eventImage =
    eventImageRaw === null || eventImageRaw === undefined
      ? null
      : readString(eventImageRaw).trim() || null;

  const descriptionRaw =
    record.eventDescription ?? record.event_description ?? null;
  const eventDescription =
    descriptionRaw === null ? '' : readString(descriptionRaw);

  return {
    eventId,
    eventCode,
    branchCode: readString(record.branchCode ?? record.branch_code),
    eventName: readString(record.eventName ?? record.event_name),
    eventDescription,
    eventImage,
    startDate: readNullableString(record.startDate ?? record.start_date),
    endDate: readNullableString(record.endDate ?? record.end_date),
    maxCapacity: readNullableNumber(
      record.maxCapacity ?? record.max_capacity,
    ),
    totalBooking: readNumber(record.totalBooking ?? record.total_booking),
    isVisible: readBoolean(record.isVisible ?? record.is_visible, true),
    isActive: readBoolean(record.isActive ?? record.is_active, true),
    isMandatory: readBoolean(
      record.isMandatory ?? record.is_mandatory,
      false,
    ),
    formCodes: readStringArray(record.formCodes ?? record.form_codes),
    weekdays: readWeekdays(record.weekdays),
    weekdayNames: readStringArray(
      record.weekdayNames ?? record.weekday_names,
    ),
    dateRanges: readDateRanges(record.dateRanges ?? record.date_ranges),
    createdAt: readString(record.createdAt ?? record.created_at),
    updatedAt: readString(record.updatedAt ?? record.updated_at),
  };
}

export function normalizeEventDate(raw: unknown): EventDateRange | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const record = raw as Record<string, unknown>;
  const startEventDate = readNullableString(
    record.startEventDate ?? record.start_event_date,
  );
  const endEventDate = readNullableString(
    record.endEventDate ?? record.end_event_date,
  );
  if (!startEventDate || !endEventDate) {
    return null;
  }

  const eventDateId =
    readNumber(record.eventDateId ?? record.event_date_id, 0) || undefined;
  const range: EventDateRange = {
    id:
      eventDateId !== undefined
        ? `edr-${String(eventDateId)}`
        : `edr-${startEventDate}-${endEventDate}`,
    startEventDate,
    endEventDate,
  };
  if (eventDateId !== undefined) {
    range.eventDateId = eventDateId;
  }
  return range;
}

export function normalizeUploadEventImageResponse(
  raw: unknown,
): UploadEventImageResponse {
  const record =
    raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};

  return {
    id: readNumber(record.id),
    fileName: readString(record.fileName),
    filePath: readString(record.filePath),
    fileType: readString(record.fileType),
    fileUrl: readString(record.fileUrl),
    storedDate: readString(record.storedDate),
  };
}

export function isPersistedEventImageUrl(
  url: string | undefined | null,
): boolean {
  if (!url || url.trim() === '') {
    return false;
  }
  return !url.startsWith('blob:');
}

export function normalizeEventsCalendar(raw: unknown): EventsCalendarMap {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }

  const record = raw as Record<string, unknown>;
  const calendar: EventsCalendarMap = {};

  for (const [dateKey, value] of Object.entries(record)) {
    if (!Array.isArray(value)) {
      calendar[dateKey] = [];
      continue;
    }

    calendar[dateKey] = value
      .map((item) => normalizeEvent(item))
      .filter((item): item is EventItem => item !== null);
  }

  return calendar;
}

/** True when the event is linked to this form, or has no formCodes (legacy). */
export function eventAppliesToForm(
  event: EventItem,
  formCode: string,
): boolean {
  const trimmed = formCode.trim();
  if (!trimmed || event.formCodes.length === 0) {
    return true;
  }
  return event.formCodes.includes(trimmed);
}
